import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { FaArrowLeft } from "react-icons/fa";
import { Button } from "../../../components/ui";
import apiService from "../../../services/apiService";
import Loader from "../../../components/ui/Loader";
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
} from "@material-tailwind/react";
import { useToast } from "../../../context/ToastContext";
import useAuthStore from "../../../store/authStore";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const EMPTY_FORM = {
  title: "",
  alias: "",
  course: "",
  stream: "",
  questions: "",
  marks: "",
  duration: "",
  taskId: "",
  subject: "",
  faculty: "",
};

const EditQSet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const {
    control,
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: EMPTY_FORM,
  });

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [courses, setCourses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [subjectLoading, setSubjectLoading] = useState(false);
  const [facultyLoading, setFacultyLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);
  const [masterDataLoaded, setMasterDataLoaded] = useState(false);

  const [open, setOpen] = useState(0);
  const [pendingSubjectId, setPendingSubjectId] = useState("");
  const [pendingFacultyId, setPendingFacultyId] = useState("");

  const initialQSetLoadedRef = useRef(false);
  const previousFacultyCourseRef = useRef("");
  const previousSubjectCourseRef = useRef("");
  const previousSubjectStreamRef = useRef("");

  const { showSuccess, showError } = useToast();
  const { user } = useAuthStore();

  const loginUser = user?.user || user?.data?.user || user?.data || user || {};

  const loginUserId =
    loginUser?.localUser?.id ||
    loginUser?.localUser?._id ||
    loginUser?.localUserId ||
    loginUser?.id ||
    loginUser?._id ||
    loginUser?.userId;

  const loginUserType = String(
    loginUser?.type || loginUser?.userType || loginUser?.role || "",
  ).toUpperCase();

  const isStaffUser = loginUserType === "STAFF";

  const selectedCourse = useWatch({
    control,
    name: "course",
  });

  const selectedStream = useWatch({
    control,
    name: "stream",
  });

  const handleOpen = (value) => setOpen(open === value ? 0 : value);

  const getArray = (result) => {
    const possibleArrays = [
      result,
      result?.data,
      result?.result,
      result?.records,
      result?.rows,
      result?.data?.data,
      result?.data?.records,
      result?.data?.rows,
      result?.result?.records,
      result?.result?.rows,
    ];

    return possibleArrays.find((item) => Array.isArray(item)) || [];
  };

  const getObject = (result) => {
    if (!result) return null;

    if (result?.data && !Array.isArray(result.data)) {
      return result.data;
    }

    if (result?.result && !Array.isArray(result.result)) {
      return result.result;
    }

    if (Array.isArray(result?.data)) {
      return result.data[0] || null;
    }

    if (Array.isArray(result)) {
      return result[0] || null;
    }

    return result;
  };

  const getId = (value) => {
    if (!value) return "";

    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }

    return String(
      value?.id ||
        value?._id ||
        value?.$oid ||
        value?.value?.id ||
        value?.value?._id ||
        "",
    );
  };

  const getTaskName = (task) => {
    return (
      task?.title ||
      task?.name ||
      task?.value ||
      task?.taskId?.title ||
      task?.taskId?.name ||
      task?.templete?.title ||
      task?.templete?.name ||
      task?.template?.title ||
      task?.template?.name ||
      task?.subject?.name ||
      "Task"
    );
  };

  const listRowFromNavigation = useMemo(() => {
    return location?.state?.qSet || null;
  }, [location?.state]);

  const applyQSetToForm = (qSet) => {
    if (!qSet) return;

    const courseId = getId(qSet.course);
    const streamId = getId(qSet.stream);
    const subjectId = getId(qSet.subject);
    const facultyId = getId(qSet.faculty);
    const taskId = getId(qSet.task || qSet.taskId);

    console.log("AIQSET_EDIT_PREFILL:", {
      courseId,
      streamId,
      subjectId,
      facultyId,
      taskId,
      courseOptions: courses.length,
      streamOptions: streams.length,
    });

    setPendingSubjectId(subjectId);
    setPendingFacultyId(facultyId);

    previousFacultyCourseRef.current = courseId;
    previousSubjectCourseRef.current = courseId;
    previousSubjectStreamRef.current = streamId;

    reset({
      title: qSet.title || "",
      alias: qSet.alias || "",
      course: courseId,
      stream: streamId,
      questions: qSet.totalQuestions ?? qSet.questions ?? qSet.totalQs ?? "",
      marks: qSet.totalMarks ?? qSet.marks ?? "",
      duration: qSet.duration ?? "",
      taskId,
      subject: subjectId,
      faculty: isStaffUser ? loginUserId || facultyId : facultyId,
    });

    initialQSetLoadedRef.current = true;
  };

  useEffect(() => {
    let isActive = true;

    const loadMasterData = async () => {
      const courseQuery = new URLSearchParams({
        option: "COURSE",
        limit: "all",
        sort: "seq ASC",
      }).toString();

      const streamQuery = new URLSearchParams({
        option: "STREAM",
        limit: "all",
        sort: "seq ASC",
      }).toString();

      try {
        const [courseResult, streamResult] = await Promise.all([
          apiService.request_old(`/i/admin/coption?${courseQuery}`, {
            method: "GET",
          }),
          apiService.request_old(`/i/admin/coption?${streamQuery}`, {
            method: "GET",
          }),
        ]);

        if (!isActive) return;

        setCourses(getArray(courseResult));
        setStreams(getArray(streamResult));
      } catch (err) {
        if (!isActive) return;

        console.log("COURSE_STREAM_LOAD_ERROR:", err);
        setCourses([]);
        setStreams([]);
      } finally {
        if (!isActive) return;
        setMasterDataLoaded(true);
      }
    };

    loadMasterData();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    const params = new URLSearchParams({
      page: "1",
      limit: "all",
      totalCount: "true",
      populateTemplate: "true",
    });

    setTaskLoading(true);

    apiService
      .request_old(`/i/ims/taskquestion?${params.toString()}`, {
        method: "GET",
      })
      .then((result) => {
        if (!isActive) return;
        setTasks(getArray(result));
      })
      .catch((err) => {
        if (!isActive) return;
        console.log(err);
        setTasks([]);
      })
      .finally(() => {
        if (!isActive) return;
        setTaskLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    if (!masterDataLoaded) {
      return () => {
        isActive = false;
      };
    }

    const loadQSet = async () => {
      if (!id) {
        showError("QSet id not found");
        setPageLoading(false);
        return;
      }

      setPageLoading(true);

      try {
        let qSet = null;

        try {
          const response = await apiService.request(`/i/ims/aiqset/${id}`, {
            method: "GET",
          });

          qSet = getObject(response);
        } catch (singleFetchError) {
          console.log("Single QSet API failed:", singleFetchError);

          const params = new URLSearchParams({
            id,
            limit: "1",
          });

          const response = await apiService.request(
            `/i/ims/aiqset?${params.toString()}`,
            {
              method: "GET",
            },
          );

          qSet = getArray(response)[0] || null;
        }

        if (!isActive) return;

        if (!qSet && listRowFromNavigation) {
          qSet = listRowFromNavigation;
        }

        if (!qSet) {
          showError("QSet data not found");
          return;
        }

        applyQSetToForm(qSet);
      } catch (err) {
        if (!isActive) return;

        console.log(err);

        if (listRowFromNavigation) {
          applyQSetToForm(listRowFromNavigation);
        } else {
          showError("QSet load failed");
        }
      } finally {
        if (!isActive) return;
        setPageLoading(false);
      }
    };

    loadQSet();

    return () => {
      isActive = false;
    };
  }, [id, listRowFromNavigation, isStaffUser, loginUserId, masterDataLoaded]);

  useEffect(() => {
    let isActive = true;

    if (!initialQSetLoadedRef.current) {
      return () => {
        isActive = false;
      };
    }

    const courseChanged =
      previousFacultyCourseRef.current &&
      previousFacultyCourseRef.current !== String(selectedCourse || "");

    if (courseChanged && !pendingFacultyId) {
      setValue("faculty", "");
    }

    previousFacultyCourseRef.current = String(selectedCourse || "");
    setFaculty([]);

    if (isStaffUser) {
      setValue("faculty", loginUserId || "");
      setFacultyLoading(false);

      return () => {
        isActive = false;
      };
    }

    const params = new URLSearchParams();

    if (selectedCourse) {
      params.append("course", selectedCourse);
    }

    params.append("limit", "all");

    setFacultyLoading(true);

    apiService
      .request_old(`/i/admin/faculty?${params.toString()}`, {
        method: "GET",
      })
      .then((result) => {
        if (!isActive) return;

        setFaculty(getArray(result));

        if (pendingFacultyId) {
          setValue("faculty", pendingFacultyId);
          setPendingFacultyId("");
        }
      })
      .catch((err) => {
        if (!isActive) return;
        console.log(err);
        setFaculty([]);
      })
      .finally(() => {
        if (!isActive) return;
        setFacultyLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [selectedCourse, setValue, isStaffUser, loginUserId, pendingFacultyId]);

  useEffect(() => {
    let isActive = true;

    if (!initialQSetLoadedRef.current) {
      return () => {
        isActive = false;
      };
    }

    const courseChanged =
      previousSubjectCourseRef.current &&
      previousSubjectCourseRef.current !== String(selectedCourse || "");

    const streamChanged =
      previousSubjectStreamRef.current &&
      previousSubjectStreamRef.current !== String(selectedStream || "");

    if ((courseChanged || streamChanged) && !pendingSubjectId) {
      setValue("subject", "");
    }

    previousSubjectCourseRef.current = String(selectedCourse || "");
    previousSubjectStreamRef.current = String(selectedStream || "");

    setSubjects([]);

    const params = new URLSearchParams();

    if (selectedCourse) {
      params.append("course", selectedCourse);
    }

    if (selectedStream) {
      params.append("stream", selectedStream);
    }

    params.append("limit", "all");

    setSubjectLoading(true);

    apiService
      .request_old(`/i/ims/subject?${params.toString()}`, {
        method: "GET",
      })
      .then((result) => {
        if (!isActive) return;

        setSubjects(getArray(result));

        if (pendingSubjectId) {
          setValue("subject", pendingSubjectId);
          setPendingSubjectId("");
        }
      })
      .catch((err) => {
        if (!isActive) return;
        console.log(err);
        setSubjects([]);
      })
      .finally(() => {
        if (!isActive) return;
        setSubjectLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [selectedCourse, selectedStream, pendingSubjectId, setValue]);

  async function handleSubmitCustom(data) {
    if (!id) {
      showError("QSet id not found");
      return;
    }

    const finalFacultyId = isStaffUser ? loginUserId : data.faculty;

    if (isStaffUser && !finalFacultyId) {
      showError("Login user id not found");
      return;
    }

    /*
     * IMPORTANT:
     * Is edit request mein koi file upload nahi ho rahi, isliye FormData mat bhejo.
     * Plain JSON bhejne se Sails/Express mein req.body ke andar course, stream,
     * subject aur faculty reliably milenge.
     */
    const payload = {
      title: String(data.title || "").trim(),
      alias: String(data.alias || "").trim(),
      course: String(data.course || ""),
      stream: String(data.stream || ""),
      totalQuestions: Number(data.questions || 0),
      totalMarks: Number(data.marks || 0),
      duration: Number(data.duration || 0),
      task: String(data.taskId || ""),
      subject: data.subject ? String(data.subject) : "",
      faculty: finalFacultyId ? String(finalFacultyId) : "",
    };

    console.log("AIQSET_UPDATE_PAYLOAD:", payload);

    setLoading(true);

    try {
      const response = await apiService.request(`/i/ims/aiqset/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log("AIQSET_UPDATE_RESPONSE:", response);

      const updatedQSet = getObject(response);

      /*
       * API kabhi success response ke andar code/status bhejti hai.
       * Explicit error aaye to success toast mat dikhana.
       */
      if (
        response?.code === "ERROR" ||
        response?.success === false ||
        response?.status === false
      ) {
        throw new Error(response?.message || "QSet update failed");
      }

      /*
       * Response mein updated record aaye to association fields verify kar lo.
       * Empty response hone par request ko successful maana jayega.
       */
      if (updatedQSet) {
        const expectedCourse = payload.course;
        const expectedStream = payload.stream;
        const expectedSubject = payload.subject;
        const expectedFaculty = payload.faculty;

        const receivedCourse = getId(updatedQSet.course);
        const receivedStream = getId(updatedQSet.stream);
        const receivedSubject = getId(updatedQSet.subject);
        const receivedFaculty = getId(updatedQSet.faculty);

        const associationMismatch =
          (expectedCourse &&
            receivedCourse &&
            expectedCourse !== receivedCourse) ||
          (expectedStream &&
            receivedStream &&
            expectedStream !== receivedStream) ||
          (expectedSubject &&
            receivedSubject &&
            expectedSubject !== receivedSubject) ||
          (expectedFaculty &&
            receivedFaculty &&
            expectedFaculty !== receivedFaculty);

        if (associationMismatch) {
          console.warn("AIQSET_ASSOCIATION_MISMATCH:", {
            expected: {
              course: expectedCourse,
              stream: expectedStream,
              subject: expectedSubject,
              faculty: expectedFaculty,
            },
            received: {
              course: receivedCourse,
              stream: receivedStream,
              subject: receivedSubject,
              faculty: receivedFaculty,
            },
          });
        }
      }

      showSuccess("QSet updated!!");
      navigate("/tools/qSet");
    } catch (err) {
      console.log("AIQSET_UPDATE_ERROR:", err);
      showError(
        err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          "Request failed",
      );
    } finally {
      setLoading(false);
    }
  }

  if (pageLoading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Edit Question Set</h1>

        <Button
          onClick={() => navigate("/tools/qSet")}
          variant="light"
          size="sm"
          icon={FaArrowLeft}
        >
          Back to List
        </Button>
      </div>

      <div className="mb-6 flex items-center text-sm">
        <span className="text-gray-500">Home</span>
        <span className="mx-2">/</span>
        <span
          onClick={() => navigate("/tools/qSet")}
          className="cursor-pointer text-gray-500"
        >
          QSet
        </span>
        <span className="mx-2">/</span>
        <span className="text-blue-600">Edit</span>
      </div>

      <div className="relative z-10 mb-6 overflow-visible rounded-lg bg-white shadow-md">
        <div className="overflow-visible p-2">
          <form onSubmit={handleSubmit(handleSubmitCustom)}>
            <div className="flex flex-col justify-around overflow-visible">
              <div className="mx-5 flex">
                <div className="w-1/2 p-3">
                  <label className="mb-2 block text-gray-700">
                    Title <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="text"
                    placeholder="Title of qSet"
                    className="w-full rounded border px-3 py-2"
                    {...register("title", {
                      required: "Title is required",
                    })}
                  />

                  {errors.title && (
                    <p className="text-sm text-red-500">
                      {errors.title.message}
                    </p>
                  )}
                </div>

                <div className="w-1/2 p-3">
                  <label className="mb-2 block text-gray-700">
                    Alias <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="text"
                    placeholder="Title Alias of qSet"
                    className="w-full rounded border px-3 py-2"
                    {...register("alias", {
                      required: "Alias is required",
                    })}
                  />

                  {errors.alias && (
                    <p className="text-sm text-red-500">
                      {errors.alias.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mx-5 flex">
                <div className="w-1/2 overflow-visible p-3">
                  <label className="mb-2 block text-gray-700">
                    Course <span className="text-red-500">*</span>
                  </label>

                  <Controller
                    name="course"
                    control={control}
                    rules={{ required: "Course is required" }}
                    render={({ field }) => (
                      <select
                        value={String(field.value || "")}
                        disabled={!masterDataLoaded}
                        onChange={(event) =>
                          field.onChange(event.target.value || "")
                        }
                        className="w-full rounded-md border border-blue-gray-200 bg-white px-3 py-2 text-gray-700 outline-none focus:border-gray-900 disabled:bg-gray-100"
                      >
                        <option value="">
                          {masterDataLoaded
                            ? "Select Course"
                            : "Loading Course..."}
                        </option>

                        {courses?.map((course, index) => {
                          const courseId = getId(course);

                          return (
                            <option key={courseId || index} value={courseId}>
                              {course.value ||
                                course.name ||
                                course.title ||
                                courseId}
                            </option>
                          );
                        })}
                      </select>
                    )}
                  />

                  {errors.course && (
                    <p className="text-sm text-red-500">
                      {errors.course.message}
                    </p>
                  )}
                </div>

                <div className="w-1/2 overflow-visible p-3">
                  <label className="mb-2 block text-gray-700">
                    Stream <span className="text-red-500">*</span>
                  </label>

                  <Controller
                    name="stream"
                    control={control}
                    rules={{ required: "Stream is required" }}
                    render={({ field }) => (
                      <select
                        value={String(field.value || "")}
                        disabled={!masterDataLoaded}
                        onChange={(event) =>
                          field.onChange(event.target.value || "")
                        }
                        className="w-full rounded-md border border-blue-gray-200 bg-white px-3 py-2 text-gray-700 outline-none focus:border-gray-900 disabled:bg-gray-100"
                      >
                        <option value="">
                          {masterDataLoaded
                            ? "Select Stream"
                            : "Loading Stream..."}
                        </option>

                        {streams?.map((stream, index) => {
                          const streamId = getId(stream);

                          return (
                            <option key={streamId || index} value={streamId}>
                              {stream.value ||
                                stream.name ||
                                stream.title ||
                                streamId}
                            </option>
                          );
                        })}
                      </select>
                    )}
                  />

                  {errors.stream && (
                    <p className="text-sm text-red-500">
                      {errors.stream.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mx-5 flex">
                <div className="w-1/2 p-3">
                  <label className="mb-2 block text-gray-700">
                    Total Questions <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="number"
                    min="0"
                    placeholder="Total Questions"
                    className="w-full rounded border px-3 py-2"
                    {...register("questions", {
                      required: "Total Questions is required",
                    })}
                  />

                  {errors.questions && (
                    <p className="text-sm text-red-500">
                      {errors.questions.message}
                    </p>
                  )}
                </div>

                <div className="w-1/2 p-3">
                  <label className="mb-2 block text-gray-700">
                    Total Marks <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="number"
                    min="0"
                    placeholder="Total Marks"
                    className="w-full rounded border px-3 py-2"
                    {...register("marks", {
                      required: "Total Marks is required",
                    })}
                  />

                  {errors.marks && (
                    <p className="text-sm text-red-500">
                      {errors.marks.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mx-5 flex">
                <div className="w-1/2 p-3">
                  <label className="mb-2 block text-gray-700">
                    Duration (in mins) <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="number"
                    min="0"
                    placeholder="Enter Duration in mins"
                    className="w-full rounded border px-3 py-2"
                    {...register("duration", {
                      required: "Duration is required",
                    })}
                  />

                  {errors.duration && (
                    <p className="text-sm text-red-500">
                      {errors.duration.message}
                    </p>
                  )}
                </div>

                <div className="w-1/2 p-3">
                  <label className="mb-2 block text-gray-700">
                    Task <span className="text-red-500">*</span>
                  </label>

                  <Controller
                    name="taskId"
                    control={control}
                    rules={{ required: "Task is required" }}
                    render={({ field }) => (
                      <select
                        value={field.value || ""}
                        disabled={taskLoading}
                        onChange={(event) =>
                          field.onChange(event.target.value || "")
                        }
                        className="w-full rounded-md border border-blue-gray-200 bg-white px-3 py-2 text-gray-700 outline-none focus:border-gray-900 disabled:bg-gray-100"
                      >
                        <option value="">
                          {taskLoading ? "Loading Task..." : "Select Task"}
                        </option>

                        {tasks?.map((task, index) => (
                          <option
                            key={task.id || task._id || index}
                            value={String(task.id || task._id)}
                          >
                            {getTaskName(task)}
                          </option>
                        ))}
                      </select>
                    )}
                  />

                  {errors.taskId && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.taskId.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="ml-5 mr-10 p-3">
                <Accordion open={open === 1} icon={<Icon id={1} open={open} />}>
                  <AccordionHeader onClick={() => handleOpen(1)}>
                    Advance Settings
                  </AccordionHeader>

                  <AccordionBody>
                    <div className="mx-5 flex">
                      <div
                        className={`p-3 ${isStaffUser ? "w-full" : "w-1/2"}`}
                      >
                        <label className="mb-2 block text-gray-700">
                          Subject
                        </label>

                        <Controller
                          name="subject"
                          control={control}
                          render={({ field }) => (
                            <select
                              value={field.value || ""}
                              disabled={!selectedCourse || subjectLoading}
                              onChange={(event) =>
                                field.onChange(event.target.value || "")
                              }
                              className="w-full rounded-md border border-blue-gray-200 bg-white px-3 py-2 text-gray-700 outline-none focus:border-gray-900 disabled:bg-gray-100"
                            >
                              <option value="">
                                {subjectLoading
                                  ? "Loading Subject..."
                                  : "Select Subject"}
                              </option>

                              {subjects?.map((subject, index) => (
                                <option
                                  key={subject.id || subject._id || index}
                                  value={String(subject.id || subject._id)}
                                >
                                  {subject.name ||
                                    subject.value ||
                                    subject.title}
                                </option>
                              ))}
                            </select>
                          )}
                        />

                        {errors.subject && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.subject.message}
                          </p>
                        )}
                      </div>

                      {!isStaffUser && (
                        <div className="w-1/2 p-3">
                          <label className="mb-2 block text-gray-700">
                            Faculty
                          </label>

                          <Controller
                            name="faculty"
                            control={control}
                            render={({ field }) => (
                              <select
                                value={field.value || ""}
                                disabled={facultyLoading}
                                onChange={(event) =>
                                  field.onChange(event.target.value || "")
                                }
                                className="w-full rounded-md border border-blue-gray-200 bg-white px-3 py-2 text-gray-700 outline-none focus:border-gray-900 disabled:bg-gray-100"
                              >
                                <option value="">
                                  {facultyLoading
                                    ? "Loading Faculty..."
                                    : "Select Faculty"}
                                </option>

                                {faculty?.map((item, index) => (
                                  <option
                                    key={item.id || item._id || index}
                                    value={String(item.id || item._id)}
                                  >
                                    {item.name || item.value || item.title}
                                  </option>
                                ))}
                              </select>
                            )}
                          />

                          {errors.faculty && (
                            <p className="mt-1 text-sm text-red-500">
                              {errors.faculty.message}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </AccordionBody>
                </Accordion>
              </div>

              <div className="p-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="m-2 rounded bg-green-500 p-2 font-thin text-white hover:bg-green-600 disabled:opacity-60"
                >
                  Update
                </button>

                {loading && <Loader />}
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-6 rounded-lg border bg-gray-50 p-4">
        <h3 className="mb-2 font-semibold">Instructions:</h3>

        <ul className="list-disc pl-5">
          <li>
            Fields marked with <span className="text-red-500">*</span> are
            mandatory.
          </li>
          <li>
            Update se existing Question/Solution upload status change nahi hoga.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default EditQSet;
// import React, { useEffect, useMemo, useRef, useState } from "react";
// import { useLocation, useNavigate, useParams } from "react-router-dom";
// import { Controller, useForm, useWatch } from "react-hook-form";
// import { FaArrowLeft } from "react-icons/fa";
// import { Button } from "../../../components/ui";
// import apiService from "../../../services/apiService";
// import Loader from "../../../components/ui/Loader";
// import {
//   Select,
//   Option,
//   Accordion,
//   AccordionHeader,
//   AccordionBody,
// } from "@material-tailwind/react";
// import { useToast } from "../../../context/ToastContext";
// import useAuthStore from "../../../store/authStore";

// function Icon({ id, open }) {
//   return (
//     <svg
//       xmlns="http://www.w3.org/2000/svg"
//       fill="none"
//       viewBox="0 0 24 24"
//       strokeWidth={2}
//       stroke="currentColor"
//       className={`${
//         id === open ? "rotate-180" : ""
//       } h-5 w-5 transition-transform`}
//     >
//       <path
//         strokeLinecap="round"
//         strokeLinejoin="round"
//         d="M19.5 8.25l-7.5 7.5-7.5-7.5"
//       />
//     </svg>
//   );
// }

// const EMPTY_FORM = {
//   title: "",
//   alias: "",
//   course: "",
//   stream: "",
//   questions: "",
//   marks: "",
//   duration: "",
//   taskId: "",
//   subject: "",
//   faculty: "",
// };

// const EditQSet = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { id } = useParams();

//   const {
//     control,
//     register,
//     reset,
//     handleSubmit,
//     setValue,
//     formState: { errors },
//   } = useForm({
//     defaultValues: EMPTY_FORM,
//   });

//   const [loading, setLoading] = useState(false);
//   const [pageLoading, setPageLoading] = useState(true);

//   const [courses, setCourses] = useState([]);
//   const [streams, setStreams] = useState([]);
//   const [subjects, setSubjects] = useState([]);
//   const [faculty, setFaculty] = useState([]);
//   const [tasks, setTasks] = useState([]);

//   const [subjectLoading, setSubjectLoading] = useState(false);
//   const [facultyLoading, setFacultyLoading] = useState(false);
//   const [taskLoading, setTaskLoading] = useState(false);

//   const [open, setOpen] = useState(0);
//   const [pendingSubjectId, setPendingSubjectId] = useState("");
//   const [pendingFacultyId, setPendingFacultyId] = useState("");

//   const initialQSetLoadedRef = useRef(false);
//   const previousFacultyCourseRef = useRef("");
//   const previousSubjectCourseRef = useRef("");
//   const previousSubjectStreamRef = useRef("");

//   const { showSuccess, showError } = useToast();
//   const { user } = useAuthStore();

//   const loginUser = user?.user || user?.data?.user || user?.data || user || {};

//   const loginUserId =
//     loginUser?.localUser?.id ||
//     loginUser?.localUser?._id ||
//     loginUser?.localUserId ||
//     loginUser?.id ||
//     loginUser?._id ||
//     loginUser?.userId;

//   const loginUserType = String(
//     loginUser?.type || loginUser?.userType || loginUser?.role || "",
//   ).toUpperCase();

//   const isStaffUser = loginUserType === "STAFF";

//   const selectedCourse = useWatch({
//     control,
//     name: "course",
//   });

//   const selectedStream = useWatch({
//     control,
//     name: "stream",
//   });

//   const handleOpen = (value) => setOpen(open === value ? 0 : value);

//   const getArray = (result) => {
//     if (Array.isArray(result)) return result;
//     if (Array.isArray(result?.data)) return result.data;
//     if (Array.isArray(result?.result)) return result.result;
//     if (Array.isArray(result?.records)) return result.records;
//     return [];
//   };

//   const getObject = (result) => {
//     if (!result) return null;

//     if (result?.data && !Array.isArray(result.data)) {
//       return result.data;
//     }

//     if (result?.result && !Array.isArray(result.result)) {
//       return result.result;
//     }

//     if (Array.isArray(result?.data)) {
//       return result.data[0] || null;
//     }

//     if (Array.isArray(result)) {
//       return result[0] || null;
//     }

//     return result;
//   };

//   const getId = (value) => {
//     if (!value) return "";

//     if (typeof value === "string" || typeof value === "number") {
//       return String(value);
//     }

//     return String(
//       value?.id ||
//         value?._id ||
//         value?.$oid ||
//         value?.value?.id ||
//         value?.value?._id ||
//         "",
//     );
//   };

//   const getTaskName = (task) => {
//     return (
//       task?.title ||
//       task?.name ||
//       task?.value ||
//       task?.taskId?.title ||
//       task?.taskId?.name ||
//       task?.templete?.title ||
//       task?.templete?.name ||
//       task?.template?.title ||
//       task?.template?.name ||
//       task?.subject?.name ||
//       "Task"
//     );
//   };

//   const listRowFromNavigation = useMemo(() => {
//     return location?.state?.qSet || null;
//   }, [location?.state]);

//   const applyQSetToForm = (qSet) => {
//     if (!qSet) return;

//     const courseId = getId(qSet.course);
//     const streamId = getId(qSet.stream);
//     const subjectId = getId(qSet.subject);
//     const facultyId = getId(qSet.faculty);
//     const taskId = getId(qSet.task || qSet.taskId);

//     setPendingSubjectId(subjectId);
//     setPendingFacultyId(facultyId);

//     previousFacultyCourseRef.current = courseId;
//     previousSubjectCourseRef.current = courseId;
//     previousSubjectStreamRef.current = streamId;

//     reset({
//       title: qSet.title || "",
//       alias: qSet.alias || "",
//       course: courseId,
//       stream: streamId,
//       questions: qSet.totalQuestions ?? qSet.questions ?? qSet.totalQs ?? "",
//       marks: qSet.totalMarks ?? qSet.marks ?? "",
//       duration: qSet.duration ?? "",
//       taskId,
//       subject: subjectId,
//       faculty: isStaffUser ? loginUserId || facultyId : facultyId,
//     });

//     initialQSetLoadedRef.current = true;
//   };

//   useEffect(() => {
//     let isActive = true;

//     const loadMasterData = async () => {
//       const courseQuery = new URLSearchParams({
//         option: "COURSE",
//         limit: "all",
//         sort: "seq ASC",
//       }).toString();

//       const streamQuery = new URLSearchParams({
//         option: "STREAM",
//         limit: "all",
//         sort: "seq ASC",
//       }).toString();

//       try {
//         const [courseResult, streamResult] = await Promise.all([
//           apiService.request_old(`/i/admin/coption?${courseQuery}`, {
//             method: "GET",
//           }),
//           apiService.request_old(`/i/admin/coption?${streamQuery}`, {
//             method: "GET",
//           }),
//         ]);

//         if (!isActive) return;

//         setCourses(getArray(courseResult));
//         setStreams(getArray(streamResult));
//       } catch (err) {
//         if (!isActive) return;

//         console.log(err);
//         setCourses([]);
//         setStreams([]);
//       }
//     };

//     loadMasterData();

//     return () => {
//       isActive = false;
//     };
//   }, []);

//   useEffect(() => {
//     let isActive = true;

//     const params = new URLSearchParams({
//       page: "1",
//       limit: "all",
//       totalCount: "true",
//       populateTemplate: "true",
//     });

//     setTaskLoading(true);

//     apiService
//       .request_old(`/i/ims/taskquestion?${params.toString()}`, {
//         method: "GET",
//       })
//       .then((result) => {
//         if (!isActive) return;
//         setTasks(getArray(result));
//       })
//       .catch((err) => {
//         if (!isActive) return;
//         console.log(err);
//         setTasks([]);
//       })
//       .finally(() => {
//         if (!isActive) return;
//         setTaskLoading(false);
//       });

//     return () => {
//       isActive = false;
//     };
//   }, []);

//   useEffect(() => {
//     let isActive = true;

//     const loadQSet = async () => {
//       if (!id) {
//         showError("QSet id not found");
//         setPageLoading(false);
//         return;
//       }

//       setPageLoading(true);

//       try {
//         let qSet = null;

//         try {
//           const response = await apiService.request(`/i/ims/aiqset/${id}`, {
//             method: "GET",
//           });

//           qSet = getObject(response);
//         } catch (singleFetchError) {
//           console.log("Single QSet API failed:", singleFetchError);

//           const params = new URLSearchParams({
//             id,
//             limit: "1",
//           });

//           const response = await apiService.request(
//             `/i/ims/aiqset?${params.toString()}`,
//             {
//               method: "GET",
//             },
//           );

//           qSet = getArray(response)[0] || null;
//         }

//         if (!isActive) return;

//         if (!qSet && listRowFromNavigation) {
//           qSet = listRowFromNavigation;
//         }

//         if (!qSet) {
//           showError("QSet data not found");
//           return;
//         }

//         applyQSetToForm(qSet);
//       } catch (err) {
//         if (!isActive) return;

//         console.log(err);

//         if (listRowFromNavigation) {
//           applyQSetToForm(listRowFromNavigation);
//         } else {
//           showError("QSet load failed");
//         }
//       } finally {
//         if (!isActive) return;
//         setPageLoading(false);
//       }
//     };

//     loadQSet();

//     return () => {
//       isActive = false;
//     };
//   }, [id, listRowFromNavigation, isStaffUser, loginUserId]);

//   useEffect(() => {
//     let isActive = true;

//     if (!initialQSetLoadedRef.current) {
//       return () => {
//         isActive = false;
//       };
//     }

//     const courseChanged =
//       previousFacultyCourseRef.current &&
//       previousFacultyCourseRef.current !== String(selectedCourse || "");

//     if (courseChanged && !pendingFacultyId) {
//       setValue("faculty", "");
//     }

//     previousFacultyCourseRef.current = String(selectedCourse || "");
//     setFaculty([]);

//     if (isStaffUser) {
//       setValue("faculty", loginUserId || "");
//       setFacultyLoading(false);

//       return () => {
//         isActive = false;
//       };
//     }

//     const params = new URLSearchParams();

//     if (selectedCourse) {
//       params.append("course", selectedCourse);
//     }

//     params.append("limit", "all");

//     setFacultyLoading(true);

//     apiService
//       .request_old(`/i/admin/faculty?${params.toString()}`, {
//         method: "GET",
//       })
//       .then((result) => {
//         if (!isActive) return;

//         setFaculty(getArray(result));

//         if (pendingFacultyId) {
//           setValue("faculty", pendingFacultyId);
//           setPendingFacultyId("");
//         }
//       })
//       .catch((err) => {
//         if (!isActive) return;
//         console.log(err);
//         setFaculty([]);
//       })
//       .finally(() => {
//         if (!isActive) return;
//         setFacultyLoading(false);
//       });

//     return () => {
//       isActive = false;
//     };
//   }, [selectedCourse, setValue, isStaffUser, loginUserId, pendingFacultyId]);

//   useEffect(() => {
//     let isActive = true;

//     if (!initialQSetLoadedRef.current) {
//       return () => {
//         isActive = false;
//       };
//     }

//     const courseChanged =
//       previousSubjectCourseRef.current &&
//       previousSubjectCourseRef.current !== String(selectedCourse || "");

//     const streamChanged =
//       previousSubjectStreamRef.current &&
//       previousSubjectStreamRef.current !== String(selectedStream || "");

//     if ((courseChanged || streamChanged) && !pendingSubjectId) {
//       setValue("subject", "");
//     }

//     previousSubjectCourseRef.current = String(selectedCourse || "");
//     previousSubjectStreamRef.current = String(selectedStream || "");

//     setSubjects([]);

//     const params = new URLSearchParams();

//     if (selectedCourse) {
//       params.append("course", selectedCourse);
//     }

//     if (selectedStream) {
//       params.append("stream", selectedStream);
//     }

//     params.append("limit", "all");

//     setSubjectLoading(true);

//     apiService
//       .request_old(`/i/ims/subject?${params.toString()}`, {
//         method: "GET",
//       })
//       .then((result) => {
//         if (!isActive) return;

//         setSubjects(getArray(result));

//         if (pendingSubjectId) {
//           setValue("subject", pendingSubjectId);
//           setPendingSubjectId("");
//         }
//       })
//       .catch((err) => {
//         if (!isActive) return;
//         console.log(err);
//         setSubjects([]);
//       })
//       .finally(() => {
//         if (!isActive) return;
//         setSubjectLoading(false);
//       });

//     return () => {
//       isActive = false;
//     };
//   }, [selectedCourse, selectedStream, pendingSubjectId, setValue]);

//   async function handleSubmitCustom(data) {
//     if (!id) {
//       showError("QSet id not found");
//       return;
//     }

//     setLoading(true);

//     const finalFacultyId = isStaffUser ? loginUserId : data.faculty;

//     if (isStaffUser && !finalFacultyId) {
//       setLoading(false);
//       showError("Login user id not found");
//       return;
//     }

//     const formData = new FormData();

//     formData.append("title", data.title);
//     formData.append("alias", data.alias);
//     formData.append("course", data.course);
//     formData.append("stream", data.stream);
//     formData.append("totalQuestions", data.questions);
//     formData.append("totalMarks", data.marks);
//     formData.append("duration", data.duration);
//     formData.append("task", data.taskId);

//     if (data.subject) {
//       formData.append("subject", data.subject);
//     } else {
//       formData.append("subject", "");
//     }

//     if (finalFacultyId) {
//       formData.append("faculty", finalFacultyId);
//     } else {
//       formData.append("faculty", "");
//     }

//     try {
//       await apiService.request(`/i/ims/aiqset/${id}`, {
//         method: "PUT",
//         body: formData,
//       });

//       showSuccess("QSet updated!!");
//       navigate("/tools/qSet");
//     } catch (err) {
//       console.log(err);
//       showError(err?.message || "Request failed");
//     } finally {
//       setLoading(false);
//     }
//   }

//   if (pageLoading) {
//     return (
//       <div className="flex min-h-[300px] items-center justify-center">
//         <Loader />
//       </div>
//     );
//   }

//   return (
//     <div className="p-4">
//       <div className="mb-6 flex items-center justify-between">
//         <h1 className="text-2xl font-bold">Edit Question Set</h1>

//         <Button
//           onClick={() => navigate("/tools/qSet")}
//           variant="light"
//           size="sm"
//           icon={FaArrowLeft}
//         >
//           Back to List
//         </Button>
//       </div>

//       <div className="mb-6 flex items-center text-sm">
//         <span className="text-gray-500">Home</span>
//         <span className="mx-2">/</span>
//         <span
//           onClick={() => navigate("/tools/qSet")}
//           className="cursor-pointer text-gray-500"
//         >
//           QSet
//         </span>
//         <span className="mx-2">/</span>
//         <span className="text-blue-600">Edit</span>
//       </div>

//       <div className="relative z-10 mb-6 overflow-visible rounded-lg bg-white shadow-md">
//         <div className="overflow-visible p-2">
//           <form onSubmit={handleSubmit(handleSubmitCustom)}>
//             <div className="flex flex-col justify-around overflow-visible">
//               <div className="mx-5 flex">
//                 <div className="w-1/2 p-3">
//                   <label className="mb-2 block text-gray-700">
//                     Title <span className="text-red-500">*</span>
//                   </label>

//                   <input
//                     type="text"
//                     placeholder="Title of qSet"
//                     className="w-full rounded border px-3 py-2"
//                     {...register("title", {
//                       required: "Title is required",
//                     })}
//                   />

//                   {errors.title && (
//                     <p className="text-sm text-red-500">
//                       {errors.title.message}
//                     </p>
//                   )}
//                 </div>

//                 <div className="w-1/2 p-3">
//                   <label className="mb-2 block text-gray-700">
//                     Alias <span className="text-red-500">*</span>
//                   </label>

//                   <input
//                     type="text"
//                     placeholder="Title Alias of qSet"
//                     className="w-full rounded border px-3 py-2"
//                     {...register("alias", {
//                       required: "Alias is required",
//                     })}
//                   />

//                   {errors.alias && (
//                     <p className="text-sm text-red-500">
//                       {errors.alias.message}
//                     </p>
//                   )}
//                 </div>
//               </div>

//               <div className="mx-5 flex">
//                 <div className="w-1/2 overflow-visible p-3">
//                   <label className="mb-2 block text-gray-700">
//                     Course <span className="text-red-500">*</span>
//                   </label>

//                   <Controller
//                     name="course"
//                     control={control}
//                     rules={{ required: "Course is required" }}
//                     render={({ field }) => (
//                       <Select
//                         key={field.value || "course"}
//                         variant="outlined"
//                         label="Select Course"
//                         value={field.value || ""}
//                         onChange={(val) => {
//                           field.onChange(val || "");
//                         }}
//                         className="z-[9999]"
//                         menuProps={{
//                           className: "z-[9999] max-h-60 overflow-auto",
//                         }}
//                       >
//                         {courses?.map((course, index) => (
//                           <Option
//                             key={course.id || course._id || index}
//                             value={String(course.id || course._id)}
//                           >
//                             {course.value || course.name || course.title}
//                           </Option>
//                         ))}
//                       </Select>
//                     )}
//                   />

//                   {errors.course && (
//                     <p className="text-sm text-red-500">
//                       {errors.course.message}
//                     </p>
//                   )}
//                 </div>

//                 <div className="w-1/2 overflow-visible p-3">
//                   <label className="mb-2 block text-gray-700">
//                     Stream <span className="text-red-500">*</span>
//                   </label>

//                   <Controller
//                     name="stream"
//                     control={control}
//                     rules={{ required: "Stream is required" }}
//                     render={({ field }) => (
//                       <Select
//                         key={field.value || "stream"}
//                         variant="outlined"
//                         label="Select Stream"
//                         value={field.value || ""}
//                         onChange={(val) => {
//                           field.onChange(val || "");
//                         }}
//                         className="z-[9999]"
//                         menuProps={{
//                           className: "z-[9999] max-h-60 overflow-auto",
//                         }}
//                       >
//                         {streams?.map((stream, index) => (
//                           <Option
//                             key={stream.id || stream._id || index}
//                             value={String(stream.id || stream._id)}
//                           >
//                             {stream.value || stream.name || stream.title}
//                           </Option>
//                         ))}
//                       </Select>
//                     )}
//                   />

//                   {errors.stream && (
//                     <p className="text-sm text-red-500">
//                       {errors.stream.message}
//                     </p>
//                   )}
//                 </div>
//               </div>

//               <div className="mx-5 flex">
//                 <div className="w-1/2 p-3">
//                   <label className="mb-2 block text-gray-700">
//                     Total Questions <span className="text-red-500">*</span>
//                   </label>

//                   <input
//                     type="number"
//                     min="0"
//                     placeholder="Total Questions"
//                     className="w-full rounded border px-3 py-2"
//                     {...register("questions", {
//                       required: "Total Questions is required",
//                     })}
//                   />

//                   {errors.questions && (
//                     <p className="text-sm text-red-500">
//                       {errors.questions.message}
//                     </p>
//                   )}
//                 </div>

//                 <div className="w-1/2 p-3">
//                   <label className="mb-2 block text-gray-700">
//                     Total Marks <span className="text-red-500">*</span>
//                   </label>

//                   <input
//                     type="number"
//                     min="0"
//                     placeholder="Total Marks"
//                     className="w-full rounded border px-3 py-2"
//                     {...register("marks", {
//                       required: "Total Marks is required",
//                     })}
//                   />

//                   {errors.marks && (
//                     <p className="text-sm text-red-500">
//                       {errors.marks.message}
//                     </p>
//                   )}
//                 </div>
//               </div>

//               <div className="mx-5 flex">
//                 <div className="w-1/2 p-3">
//                   <label className="mb-2 block text-gray-700">
//                     Duration (in mins) <span className="text-red-500">*</span>
//                   </label>

//                   <input
//                     type="number"
//                     min="0"
//                     placeholder="Enter Duration in mins"
//                     className="w-full rounded border px-3 py-2"
//                     {...register("duration", {
//                       required: "Duration is required",
//                     })}
//                   />

//                   {errors.duration && (
//                     <p className="text-sm text-red-500">
//                       {errors.duration.message}
//                     </p>
//                   )}
//                 </div>

//                 <div className="w-1/2 p-3">
//                   <label className="mb-2 block text-gray-700">
//                     Task <span className="text-red-500">*</span>
//                   </label>

//                   <Controller
//                     name="taskId"
//                     control={control}
//                     rules={{ required: "Task is required" }}
//                     render={({ field }) => (
//                       <select
//                         value={field.value || ""}
//                         disabled={taskLoading}
//                         onChange={(event) =>
//                           field.onChange(event.target.value || "")
//                         }
//                         className="w-full rounded-md border border-blue-gray-200 bg-white px-3 py-2 text-gray-700 outline-none focus:border-gray-900 disabled:bg-gray-100"
//                       >
//                         <option value="">
//                           {taskLoading ? "Loading Task..." : "Select Task"}
//                         </option>

//                         {tasks?.map((task, index) => (
//                           <option
//                             key={task.id || task._id || index}
//                             value={String(task.id || task._id)}
//                           >
//                             {getTaskName(task)}
//                           </option>
//                         ))}
//                       </select>
//                     )}
//                   />

//                   {errors.taskId && (
//                     <p className="mt-1 text-sm text-red-500">
//                       {errors.taskId.message}
//                     </p>
//                   )}
//                 </div>
//               </div>

//               <div className="ml-5 mr-10 p-3">
//                 <Accordion open={open === 1} icon={<Icon id={1} open={open} />}>
//                   <AccordionHeader onClick={() => handleOpen(1)}>
//                     Advance Settings
//                   </AccordionHeader>

//                   <AccordionBody>
//                     <div className="mx-5 flex">
//                       <div
//                         className={`p-3 ${isStaffUser ? "w-full" : "w-1/2"}`}
//                       >
//                         <label className="mb-2 block text-gray-700">
//                           Subject
//                         </label>

//                         <Controller
//                           name="subject"
//                           control={control}
//                           render={({ field }) => (
//                             <select
//                               value={field.value || ""}
//                               disabled={!selectedCourse || subjectLoading}
//                               onChange={(event) =>
//                                 field.onChange(event.target.value || "")
//                               }
//                               className="w-full rounded-md border border-blue-gray-200 bg-white px-3 py-2 text-gray-700 outline-none focus:border-gray-900 disabled:bg-gray-100"
//                             >
//                               <option value="">
//                                 {subjectLoading
//                                   ? "Loading Subject..."
//                                   : "Select Subject"}
//                               </option>

//                               {subjects?.map((subject, index) => (
//                                 <option
//                                   key={subject.id || subject._id || index}
//                                   value={String(subject.id || subject._id)}
//                                 >
//                                   {subject.name ||
//                                     subject.value ||
//                                     subject.title}
//                                 </option>
//                               ))}
//                             </select>
//                           )}
//                         />

//                         {errors.subject && (
//                           <p className="mt-1 text-sm text-red-500">
//                             {errors.subject.message}
//                           </p>
//                         )}
//                       </div>

//                       {!isStaffUser && (
//                         <div className="w-1/2 p-3">
//                           <label className="mb-2 block text-gray-700">
//                             Faculty
//                           </label>

//                           <Controller
//                             name="faculty"
//                             control={control}
//                             render={({ field }) => (
//                               <select
//                                 value={field.value || ""}
//                                 disabled={facultyLoading}
//                                 onChange={(event) =>
//                                   field.onChange(event.target.value || "")
//                                 }
//                                 className="w-full rounded-md border border-blue-gray-200 bg-white px-3 py-2 text-gray-700 outline-none focus:border-gray-900 disabled:bg-gray-100"
//                               >
//                                 <option value="">
//                                   {facultyLoading
//                                     ? "Loading Faculty..."
//                                     : "Select Faculty"}
//                                 </option>

//                                 {faculty?.map((item, index) => (
//                                   <option
//                                     key={item.id || item._id || index}
//                                     value={String(item.id || item._id)}
//                                   >
//                                     {item.name || item.value || item.title}
//                                   </option>
//                                 ))}
//                               </select>
//                             )}
//                           />

//                           {errors.faculty && (
//                             <p className="mt-1 text-sm text-red-500">
//                               {errors.faculty.message}
//                             </p>
//                           )}
//                         </div>
//                       )}
//                     </div>
//                   </AccordionBody>
//                 </Accordion>
//               </div>

//               <div className="p-3">
//                 <button
//                   type="submit"
//                   disabled={loading}
//                   className="m-2 rounded bg-green-500 p-2 font-thin text-white hover:bg-green-600 disabled:opacity-60"
//                 >
//                   Update
//                 </button>

//                 {loading && <Loader />}
//               </div>
//             </div>
//           </form>
//         </div>
//       </div>

//       <div className="mt-6 rounded-lg border bg-gray-50 p-4">
//         <h3 className="mb-2 font-semibold">Instructions:</h3>

//         <ul className="list-disc pl-5">
//           <li>
//             Fields marked with <span className="text-red-500">*</span> are
//             mandatory.
//           </li>
//           <li>
//             Update se existing Question/Solution upload status change nahi hoga.
//           </li>
//         </ul>
//       </div>
//     </div>
//   );
// };

// export default EditQSet;
